import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO/SEO";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { MapComponent } from "../contact/components/MapComponent";
import { homeStore } from "@/_zustand/home";
import { ListaMaquinasByDistance } from "@/hooks/useMaquinas";
import {
  useTiendasVisibles,
  estadoDeQuery,
} from "@/hooks/useTiendasVisibles";
import {
  filtrarSinLicor,
  textoFallbackFavoritas,
  textoListadoVacio,
  TIENDAS_MSG_SIN_FAVORITAS_EMPRESA,
  TIENDAS_MSG_SIN_FAVORITAS_GENERAL,
} from "@/lib/tiendasVisibles";
import { useListaEmpresas } from "@/hooks/useEmpresas";
import { useMachineNavigation } from "@/hooks/useMachineNavigation";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import * as _ from "lodash";
import Empty from "@/assets/imgs/empty.png";
import { useFavoriteStore } from "@/_zustand/favorite";
import { useAuth } from "@/_zustand/auth";

export const Stores = () => {
  const navigate = useNavigate();
  const { getCompanyRoute } = useCompanyFlow();
  const { goToMachine } = useMachineNavigation();
  const { tiendaId, setStore } = homeStore();
  const { userAddress } = useAuth();
  const latitud = userAddress?.length === 2 ? Number(userAddress[0]) : -33.4369;
  const longitud =
    userAddress?.length === 2 ? Number(userAddress[1]) : -70.6693;

  const {
    data: response,
    refetch,
    isSuccess,
    isError,
  } = ListaMaquinasByDistance(latitud, longitud, tiendaId);
  // La query es `enabled:false` + refetch desde un efecto, así que hay un
  // momento sin datos en cada montaje y, con `retry:false`, un error la deja
  // vacía para siempre. Ese estado se propaga para no afirmar "no hay tiendas
  // cerca" cuando en realidad todavía no cargó o falló.
  const estadoCercanas = estadoDeQuery({ isSuccess, isError });

  // Ordenar por distancia ascendente (incidencia #4)
  const maquinasResponse =
    response && response.status === "success" && Array.isArray(response.data)
      ? [...response.data].sort(
          (a, b) => parseFloat(a.distancia || 0) - parseFloat(b.distancia || 0),
        )
      : [];

  const sliderRef = useRef(null);
  const { addFavorite, removeFavorite, favorites } = useFavoriteStore();
  const [isStoreFavorite, setIsStoreFavorite] = useState(false);
  const [mostrarFavoritas, setMostrarFavoritas] = useState(false);

  // Favoritas vigentes + fallback cuando la empresa no tiene tiendas dentro
  // del radio: toda la lógica (y el porqué) en src/lib/tiendasVisibles.js,
  // compartida con StoresCompany.jsx para no volver a tener dos copias que
  // divergen.
  const { favoritasDelContexto, tiendasAMostrar, motivoFallback } =
    useTiendasVisibles({
      maquinasCercanas: maquinasResponse,
      tiendaId,
      mostrarFavoritas,
      estadoCercanas,
    });
  const avisoFallback = textoFallbackFavoritas({ motivoFallback, tiendaId });

  useEffect(() => {
    refetch();
  }, [latitud, longitud, tiendaId, refetch]);

  const StoreLogos = () => {
    const { data: empresas, isLoading, error } = useListaEmpresas();
    if (isLoading) return <div>Cargando logos...</div>;
    if (error) return <div>Error al cargar logos.</div>;
    return (
      <Slider ref={sliderRef} {...settings}>
        {_.size(empresas) > 0 &&
          empresas.map((item, index) => (
            <div key={index}>
              <img
                className="shadow p-2 img-fluid"
                onClick={() => tiendaClick(item)}
                src={item.logo_empresa}
                alt={item.empresa_nombre}
                style={{
                  width: "58px", // Ancho predeterminado
                  height: "58px", // Alto predeterminado
                  objectFit: "scale-down", // Ajusta la imagen para cubrir el área establecida
                  borderRadius: "50%",
                }}
              />
            </div>
          ))}
      </Slider>
    );
  };

  const machineClick = async (machine) => {
    await goToMachine(machine.maquina_id);
  };

  const tiendaClick = (tienda) => {
    setStore(tienda.empresa_id);
    navigate(getCompanyRoute(`stores/${tienda.empresa_id}`), {
      replace: false,
    });
  };

  const StoreList = (stores) => {
    // Usa las favoritas ya validadas y acotadas a la empresa en contexto, no
    // el `favorites` crudo: con el crudo, ver la empresa A en la pestaña de
    // favoritas listaba también las máquinas favoritas de la empresa B.
    const tiendas = mostrarFavoritas ? favoritasDelContexto : stores.stores;

    // Pestaña de favoritas sin ninguna que mostrar: antes esta rama era
    // inalcanzable (sólo se evaluaba con `_.size(tiendas) > 0`, o sea nunca
    // cuando la lista estaba vacía) y el componente devolvía undefined, así
    // que el usuario terminaba en el corte de página sin pestañas — un
    // callejón sin salida del que no podía volver a "Todas las tiendas".
    if (mostrarFavoritas && _.size(tiendas) === 0) {
      return (
        <div className="row">
          <p className="text-center text-black px-3 ">
            {Number(tiendaId) > 0
              ? TIENDAS_MSG_SIN_FAVORITAS_EMPRESA
              : TIENDAS_MSG_SIN_FAVORITAS_GENERAL}
          </p>
          <img
            className="img-fluid"
            src={Empty}
            alt="store-empty"
            style={{ objectFit: "scale-down" }}
          />
        </div>
      );
    }

    if (_.size(tiendas) > 0 && tiendaId != 0) {
      return (
        <div className="store-list">
          {filtrarSinLicor(tiendas).map((store, index) => (
            <MachineCard key={store.maquina_id} store={store} index={index} />
          ))}
        </div>
      );
    } else if (_.size(tiendas) > 0 && tiendaId == 0) {
      return (
        <div className="store-list">
          {filtrarSinLicor(_.uniqBy(tiendas, "maquina_id")).map(
            (store, index) => (
              <MachineCard key={store.maquina_id} store={store} index={index} />
            ),
          )}
        </div>
      );
    }
  };

  const MachineCard = (store, index) => {
    const storeCard = store.store;
    const hasOfertaDelDia = storeCard.monto_fijo || storeCard.porcentaje;
    const hasOferta2x1 = storeCard.oferta2x1;
    const [isFavorite, setIsFavorite] = useState(false);
    useEffect(() => {
      const isFav = favorites.some(
        (fav) => fav.maquina_id === storeCard.maquina_id,
      );

      setIsStoreFavorite(isFav);
      setIsFavorite(isFav);
    }, [storeCard.maquina_id]);

    const handleFavoriteClick = (event) => {
      // Evita que el clic en el corazón active el evento del contenedor de la tarjeta.
      event.stopPropagation();

      // Si la tienda ya es favorita, la elimina; si no, la agrega.
      if (isStoreFavorite) {
        toast.success(
          `Se elimina como favorita la tienda ${storeCard.maquina_nombre}`,
        );
        removeFavorite(storeCard.maquina_id);
      } else {
        toast.success(
          `Se marca como favorita la tienda ${storeCard.maquina_nombre}`,
        );
        addFavorite(storeCard);
      }
    };

    if (
      storeCard.empresa_id == tiendaId ||
      (tiendaId == 0 && storeCard.vende_licor == 0)
    ) {
      return (
        <div
          key={index}
          className={`store-card ${store.highlighted ? "highlighted" : ""}`}
          onClick={() => machineClick(storeCard)} // Este onClick se mantiene para la tarjeta
          style={{ position: "relative" }} // Necesario para posicionar el corazón si usa absolute
        >
          <img
            src={storeCard.logo_empresa}
            alt={storeCard.empresa_nombre || storeCard.maquina_nombre}
            className="store-image"
          />

          <div className="store-info">
            <h3 className="store-name">{storeCard.maquina_nombre}</h3>{" "}
            <p className="store-location">{storeCard.region_nombre}</p>{" "}
            <p className="store-address">
              📍{" "}
              <span className="distance">
                {storeCard.maquina_direccion || "Dirección no disponible"}
              </span>{" "}
              {storeCard.comuna_nombre}
            </p>
            <div className="store-tags">
              {hasOfertaDelDia && (
                <span className="store-tag">Ofertas del día</span>
              )}
              {hasOferta2x1 && (
                <span className="store-tag store-tag-2x1">2x1</span>
              )}
            </div>
          </div>

          {/* El corazón ahora tiene un onClick directo */}
          <i
            //className="fav-heart"
            className={` ${isFavorite ? "fav-heart-fill" : "fav-heart"}`}
            onClick={handleFavoriteClick}
            style={{
              cursor: "pointer",
            }}
          />
        </div>
      );
    }
  };

  const SampleNextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          background: "#80808061",
          borderRadius: "50%",
        }}
        onClick={onClick}
      />
    );
  };

  const SamplePrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          background: "#80808061",
          borderRadius: "50%",
        }}
        onClick={onClick}
      />
    );
  };

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 4,
    autoplay: true,
    autoplaySpeed: 2500,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const StoreButtons = ({ mostrarFavoritas, setMostrarFavoritas }) => {
    return (
      <div className="store-buttons">
        <button
          className={`store-btn ${!mostrarFavoritas ? "active" : ""}`}
          onClick={() => setMostrarFavoritas(false)}
        >
          Todas las tiendas
        </button>
        <button
          className={`store-btn ${mostrarFavoritas ? "active" : ""}`}
          onClick={() => setMostrarFavoritas(true)}
        >
          <i className="heart" /> Mis tiendas favoritas
        </button>
      </div>
    );
  };

  // Sólo se corta la página cuando de verdad no hay nada que mostrar: ni
  // tiendas dentro del radio ni favoritas guardadas de esta empresa. Antes
  // este corte se disparaba con la respuesta filtrada por radio vacía, así
  // que entrar a una empresa desde "Mis tiendas" del Home terminaba en esta
  // pantalla sin carrusel ni mapa, incluso teniendo favoritas suyas.
  //
  // Nunca se corta estando en la pestaña de favoritas: esta pantalla no
  // incluye los botones de pestaña, así que cortar ahí dejaba al usuario sin
  // forma de volver a "Todas las tiendas". Ese caso lo maneja StoreList con
  // su propio mensaje, conservando la navegación.
  if (_.size(tiendasAMostrar) == 0 && !mostrarFavoritas) {
    return (
      <section className="container my-5" style={{ flexGrow: 1 }}>
        <div className="row d-flex justify-content-center">
          <h5 className="mt-0 inter-bold about-us-title-1 text-start">
            Tiendas
          </h5>
          <div
            className="col-md-12 col-12 mb-md-0 p-md-4 text-center"
            style={{ minHeight: "400px" }}
          >
            <img className="img-fluid" src={Empty} alt="store-empty" />
            <p className="text-center text-black px-3 text-info ">
              {textoListadoVacio({ tiendaId, estadoCercanas })}
              <i className="icon-location"></i>
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="container my-5" style={{ flexGrow: 1 }}>
      <SEO
        title="Tiendas disponibles"
        description="Encuentra todas las tiendas vending disponibles cerca de ti."
        url="/stores"
      />
      <div className="row d-flex justify-content-center">
        <h5 className="mt-0 inter-bold about-us-title-1 text-start">
          {mostrarFavoritas ? "Mis tiendas Favoritas" : "Tiendas"}
        </h5>
        <div className="col-md-4 col-12 mb-md-0 p-md-4 col-max-height">
          <StoreButtons
            mostrarFavoritas={mostrarFavoritas}
            setMostrarFavoritas={setMostrarFavoritas}
          />
          <StoreLogos />
          <div>
            {avisoFallback && (
              <p className="text-black px-1 mb-2" style={{ fontSize: "14px" }}>
                {avisoFallback}
              </p>
            )}
            <StoreList stores={tiendasAMostrar} />
          </div>
        </div>
        <div className="col-md-8 p-2 ps-md-0 col-max-height">
          <MapComponent />
        </div>
      </div>
    </section>
  );
};
